import {
  Box,
  Chip,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../../locales/messages";
import {
  GfFlyerReviewChangeRequest,
} from "@swagger/typescript-fetch-goflyer";
import { ReviewChangeRequest } from "../../types";
import { reviewTokens } from "../../theme";
import { ItemActivityGroup } from "./useReviewActivity";
import { ItemSummary } from "./useFlyerItemSummaries";
import { CrSubmitterResolver } from "./useCrSubmitter";
import { buildHistorySlots } from "./buildHistorySlots";
import { HistoryTree } from "./HistoryTree";

interface Props {
  open: boolean;
  onClose: () => void;
  groups: ItemActivityGroup[];
  summaries: Map<string, ItemSummary | "loading">;
  flyerPublicationStatus?: string;
  resolveSubmitter: CrSubmitterResolver;
  busyIds: string[];
  onAccept: (cr: ReviewChangeRequest) => void;
  onReject: (cr: ReviewChangeRequest) => void;
  onRestore: (cr: ReviewChangeRequest) => void;
}

const St = GfFlyerReviewChangeRequest.StatusEnum;

type FilterId = "ALL" | GfFlyerReviewChangeRequest.StatusEnum;

interface FilterDef {
  id: FilterId;
  labelKey: () => ReturnType<typeof messages.All>;
}

const FILTER_DEFS: FilterDef[] = [
  { id: "ALL", labelKey: () => messages.All() },
  { id: St.PENDING, labelKey: () => messages.Cr_Status_Pending() },
  { id: St.ACCEPTED, labelKey: () => messages.Cr_Status_Accepted() },
  { id: St.REJECTED, labelKey: () => messages.Cr_Status_Rejected() },
  { id: St.SUPERSEDED, labelKey: () => messages.Cr_Status_Superseded() },
  { id: St.RESTORED, labelKey: () => messages.Cr_Status_Restored() },
];

export function FullHistoryDrawer({
  open,
  onClose,
  groups,
  summaries,
  flyerPublicationStatus,
  resolveSubmitter,
  busyIds,
  onAccept,
  onReject,
  onRestore,
}: Props) {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<FilterId>("ALL");

  const itemNameById = useMemo(() => {
    const m = new Map<string, string | undefined>();
    summaries.forEach((s, id) => {
      if (s && s !== "loading") {
        const trimmed = (s.name || "").trim();
        m.set(id, trimmed || undefined);
      }
    });
    return m;
  }, [summaries]);

  const allChangeRequests = useMemo(
    () => groups.flatMap((g) => g.changeRequests),
    [groups],
  );

  const filteredCrs = useMemo(() => {
    if (filter === "ALL") return allChangeRequests;
    return allChangeRequests.filter((cr) => cr.status === filter);
  }, [allChangeRequests, filter]);

  const slots = useMemo(
    () =>
      buildHistorySlots({
        changeRequests: filteredCrs,
        itemNameById,
      }),
    [filteredCrs, itemNameById],
  );

  const filterCounts = useMemo(() => {
    const counts: Record<FilterId, number> = {
      ALL: allChangeRequests.length,
      [St.PENDING]: 0,
      [St.ACCEPTED]: 0,
      [St.REJECTED]: 0,
      [St.SUPERSEDED]: 0,
      [St.RESTORED]: 0,
    };
    for (const cr of allChangeRequests) {
      const k = cr.status as FilterId;
      counts[k] = (counts[k] || 0) + 1;
    }
    return counts;
  }, [allChangeRequests]);

  const jumpTo = (flyerItemId: string, fieldKey: string) => {
    const slotKey = `${flyerItemId}-${fieldKey}`;
    const el = document.querySelector(
      `[data-testid="full-history-drawer"] [data-slot-key="${slotKey}"]`,
    );
    if (el && (el as HTMLElement).scrollIntoView) {
      (el as HTMLElement).scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: "100%", md: "min(960px, 100vw)" } },
      }}
      data-testid="full-history-drawer"
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: `1px solid ${reviewTokens.border}`,
          bgcolor: reviewTokens.bgPaper,
          position: "sticky",
          top: 0,
          zIndex: 2,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <AccountTreeOutlinedIcon
            sx={{ fontSize: 20, color: reviewTokens.brand }}
          />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 15 }}>
              {t(messages.Full_Change_History())}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: 11 }}
            >
              {t(messages.Restore_Eligibility_Help())}
            </Typography>
          </Box>
        </Stack>
        <IconButton size="small" onClick={onClose} aria-label="close">
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Stack>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "220px minmax(0, 1fr)" },
          gap: 0,
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            borderRight: `1px solid ${reviewTokens.border}`,
            bgcolor: reviewTokens.slate50,
            overflow: "auto",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              px: 2,
              pt: 2,
              pb: 1,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: "text.secondary",
            }}
          >
            {t(messages.History())}
          </Typography>
          <List dense disablePadding sx={{ pb: 2 }}>
            {slots.length === 0 ? (
              <Typography
                variant="caption"
                sx={{ px: 2, color: "text.secondary" }}
              >
                {t(messages.History_Empty_Title())}
              </Typography>
            ) : (
              slots.map((slot) => (
                <ListItemButton
                  key={`${slot.flyerItemId}-${slot.fieldKey}`}
                  onClick={() => jumpTo(slot.flyerItemId, slot.fieldKey)}
                  data-testid={`full-history-jump-${slot.flyerItemId}-${slot.fieldKey}`}
                  sx={{ px: 2, py: 0.75 }}
                >
                  <Stack spacing={0.25} sx={{ minWidth: 0, width: "100%" }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "text.primary",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {slot.itemName ||
                        slot.flyerItemId.slice(0, 8) + "…"}
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontFamily: reviewTokens.fontMono,
                          fontSize: 10,
                          color: "text.secondary",
                        }}
                      >
                        {slot.fieldKey}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: 10,
                          color: "text.disabled",
                        }}
                      >
                        · {slot.crs.length}
                      </Typography>
                    </Stack>
                  </Stack>
                </ListItemButton>
              ))
            )}
          </List>
        </Box>

        <Box sx={{ overflow: "auto", display: "flex", flexDirection: "column" }}>
          <Stack
            direction="row"
            spacing={0.75}
            flexWrap="wrap"
            sx={{
              px: 2.5,
              pt: 2,
              pb: 1,
              rowGap: 0.75,
            }}
          >
            {FILTER_DEFS.map((f) => {
              const active = filter === f.id;
              const count = filterCounts[f.id] || 0;
              return (
                <Chip
                  key={f.id}
                  size="small"
                  label={`${t(f.labelKey())} ${count}`}
                  onClick={() => setFilter(f.id)}
                  variant={active ? "filled" : "outlined"}
                  data-testid={`full-history-filter-${String(f.id).toLowerCase()}`}
                  sx={{
                    cursor: "pointer",
                    bgcolor: active ? "text.primary" : "background.paper",
                    color: active ? "background.paper" : "text.secondary",
                    borderColor: active ? "text.primary" : "divider",
                    "& .MuiChip-label": {
                      color: active ? "background.paper" : "text.secondary",
                    },
                  }}
                />
              );
            })}
          </Stack>
          <Box sx={{ px: 2.5, pb: 3 }}>
            <HistoryTree
              slots={slots}
              flyerPublicationStatus={flyerPublicationStatus}
              resolveSubmitter={resolveSubmitter}
              busyIds={busyIds}
              onAccept={onAccept}
              onReject={onReject}
              onRestore={onRestore}
            />
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
}
